import { useIntl } from "react-intl";

import FormItem from "forms/fields/FormItem";
import parseDictionary from "forms/helpers/parseDictionary";
import type { FormControlType } from "types/formType";
import type { IProperty } from "types/jsonSchema";
import { predefinedFields } from "types/predefinedFields";
import { Fieldset } from "ui/Fieldset";
import { Select } from "ui/Select/Select";
import { FormField } from "uiShadcn/form";

export interface ExpectedSalaryProps {
    control: FormControlType;
    expectedSalary: IProperty;
    dictionaryReferenceProperty: string;
}

function getCurrency(str: string) {
    const segments = str?.split("-");
    return segments.pop();
}

export default function ExpectedSalary({ control, expectedSalary, dictionaryReferenceProperty }: ExpectedSalaryProps) {
    const { formatMessage } = useIntl();

    if (!expectedSalary) return null;

    const isRequired = expectedSalary.type === "integer";

    const currency = getCurrency(dictionaryReferenceProperty);
    const title = `${formatMessage({ id: "expectedSalary" })} ${formatMessage({ id: currency })}`;

    const options = parseDictionary({
        enumNames: expectedSalary?.["x-enumNames"] ?? [],
        enumValues: expectedSalary?.enum ?? [],
    });

    const dictionary = Object.entries(options).map(([value, label]) => ({
        key: value,
        value: label,
    }));

    return (
        <Fieldset title={title}>
            <FormField
                control={control}
                name={predefinedFields.expectedSalary}
                render={({ field }) => (
                    <FormItem
                        label={formatMessage({
                            id: "expectedSalaryDesc",
                        })}
                        required={isRequired}
                    >
                        <Select
                            ref={field.ref}
                            dataTestId="Select-ExpectedSalary"
                            defaultValue={field.value}
                            onValueChange={field.onChange}
                            required={isRequired}
                            options={dictionary}
                            placeholder={formatMessage({ id: "select" })}
                        />
                    </FormItem>
                )}
            />
        </Fieldset>
    );
}
